import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { supabase } from '@/lib/supabaseClient';

const ScheduleVisitSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: supaError } = await supabase.from('bookings').insert({
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferred_date: formData.date || null,
        preferred_time: formData.time || null,
        notes: formData.message,
        status: 'new',
        source: 'schedule_visit_section',
      });
      if (supaError) throw supaError;
      setSuccess('Visit request submitted successfully!');
      setFormData({ name: '', email: '', phone: '', date: '', time: '', message: '' });
    } catch (err) {
      console.error('Failed to submit visit request:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[0.25em] title-font text-[#81be00] mb-6 leading-tight">
            Schedule Your Visit
          </h2>
          <p className="text-stone-500 text-base max-w-2xl mx-auto">
            Book a walk-through of our projects and learn how Nyinimu makes daily payments work for your dream home.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Map / Info Column */}
          <div className="space-y-6">
            <Card className="shadow-lg border-0 rounded-none overflow-hidden">
              <div className="h-80 w-full">
                <iframe
                  title="Nyinimu Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.858023076083!2d32.58252057583611!3d0.34759639965362085!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbb3862d0a589%3A0xa2ddedfb333b0e80!2sNyinimu%20Properties!5e0!3m2!1sen!2sug!4v1763142400000!5m2!1sen!2sug"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-2xl font-semibold text-stone-800">Visit Our Experience Center</h3>
                <p className="text-stone-600">
                  Find us in Kampala where we showcase our house plans, materials, and the entire Nyinimu ownership journey.
                </p>
                <div className="space-y-2 text-stone-700">
                  <p><strong>Address:</strong> Kampala, Uganda</p>
                  <p><strong>Hours:</strong> Mon–Sat, 9:00 AM – 6:00 PM</p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    <a href="tel:+256200920200" className="text-[#81be00] hover:text-[#6a9e00]">
                      +256-200-920200
                    </a>
                    {' / '}
                    <a href="tel:+256762534774" className="text-[#81be00] hover:text-[#6a9e00]">
                      +256-762-534774
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Form Column */}
          <Card className="shadow-lg border-0 rounded-none">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Full name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      E-mail
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      dd/mm/yyyy
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      placeholder="Select date"
                      className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      placeholder="Select time"
                      className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter your message"
                    className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
                    required
                  />
                </div>

                <div className="text-center pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#81be00] hover:bg-amber-700 text-white py-3 text-lg font-medium uppercase tracking-wide transition-all duration-300"
                  >
                    {submitting ? 'Submitting...' : 'Schedule Visit'}
                  </Button>
                  {success && <p className="text-sm text-green-600 mt-3">{success}</p>}
                  {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
                  <p className="text-sm text-stone-600 mt-4">
                    Or call us directly:
                    <strong className="block">
                      <a href="tel:+256200920200" className="text-[#81be00] hover:text-[#6a9e00]">
                        +256-200-920200
                      </a>{' '}
                      |{' '}
                      <a href="tel:+256762534774" className="text-[#81be00] hover:text-[#6a9e00]">
                        +256-762-534774
                      </a>
                    </strong>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ScheduleVisitSection;
